/**
 * Agenda modal — timeline-style list of agenda items (time + label +
 * duration) for the current workshop.
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 376-396 — `#agendaModal` shell + footer.
 *   - `_legacy/xaes-content.js` lines 449-518 — `renderAgendaModal()`
 *     which emits a row per session with time, title, detail, and
 *     duration pill.
 *   - `_legacy/ws4-session.js` lines 28-35 — agenda header (date,
 *     venue, time range).
 *
 * Chrome contract:
 *   - Uses `PanelDialogContent` + the legacy `.modal-shell` markup so
 *     this briefing modal matches the Wave-4 Asset Locker output modals
 *     (AnalysisModal, ReportModal, PlaybackModal). The shadcn `Dialog`
 *     still drives accessibility (focus trap, ESC, a11y title/desc).
 *   - Body content (timeline rows) is unchanged — only the outer chrome
 *     was swapped from `DialogContent` to `.modal-shell`.
 */

import { Calendar, Clock, MapPin, X } from "lucide-react";
import { WorkshopLabelPill, workshopLabelColor } from "@/components/tags/WorkshopLabelPill";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import type { AgendaContent, AgendaItem } from "./modal-content";

type AgendaModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
  agenda: AgendaContent;
};

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `${hours} hr` : `${hours} hr ${rem} min`;
}

function totalDuration(sessions: AgendaItem[]): string {
  const total = sessions.reduce((sum, s) => sum + s.durationMin, 0);
  return formatDuration(total);
}

function AgendaRow({ item }: { item: AgendaItem }) {
  const isBreak = item.type === "break";
  return (
    <li
      className={cn(
        "border-border/40 bg-card/60 flex items-start gap-4 rounded-md border p-3",
        isBreak && "bg-secondary/40"
      )}
    >
      <span
        className={cn(
          "text-foreground font-mono text-sm tabular-nums",
          "flex-shrink-0 pt-0.5",
          isBreak ? "text-muted-foreground" : "text-true-blue"
        )}
      >
        {item.time}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            "text-foreground text-sm font-semibold",
            isBreak && "text-muted-foreground font-medium"
          )}
        >
          {item.title}
        </span>
        <span className="text-muted-foreground text-xs">{item.detail}</span>
      </div>
      <span
        className={cn(
          "flex-shrink-0 rounded-full px-2 py-0.5 text-fs-label font-semibold tracking-wider uppercase",
          isBreak
            ? "bg-secondary/60 text-muted-foreground"
            : "text-true-blue"
        )}
        style={
          isBreak
            ? undefined
            : { backgroundColor: "hsl(var(--accent-true-blue-hsl) / 0.12)" }
        }
      >
        {formatDuration(item.durationMin)}
      </span>
    </li>
  );
}

export function AgendaModal({
  open,
  onOpenChange,
  workshopId,
  agenda,
}: AgendaModalProps) {
  const sessionCount = agenda.sessions.filter((s) => s.type === "session").length;
  const total = totalDuration(agenda.sessions);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[820px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">
          Workshop Agenda — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Session-by-session agenda for {workshopId}, including times, titles,
          and durations. {sessionCount} sessions · {total} total.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER — legacy `.modal-header` is `space-between` flex:
              title block on the LEFT, action cluster on the right. */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Calendar
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">WORKSHOP AGENDA</span>
              <WorkshopLabelPill workshopId={workshopId} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                {sessionCount} sessions · {total}
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY — `.dt-pane.dt-pane-active` provides the 16 px gutter. */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 pb-3 text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                  {agenda.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                  {agenda.venue}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                  {agenda.timeRange}
                </span>
              </div>
              <ol className="flex flex-col gap-2">
                {agenda.sessions.map((item) => (
                  <AgendaRow key={`${item.time}-${item.title}`} item={item} />
                ))}
              </ol>
            </div>
          </div>

          {/* FOOTER — legacy `.modal-footer` chrome */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Workshop Brief — Agenda</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: workshopLabelColor(workshopId) }}
            >
              {workshopId} — Human-Agent Ways of Working
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
